import { Flex, Image, Text } from '@chakra-ui/react';
import { Card } from '@ui/components/Card';
import DesktopBg from '@ui/components/DesktopBg';
import HowItWorksDrawer from '@ui/components/HowItWorksDrawer';
import { StakeModeSwitchTab } from '@ui/components/StakeModeSwitchTab';
import WithWallet from '@ui/components/Wallet/WithWallet';
import { pathDict } from '@ui/config/paths';
import { getPathInfo, useRealPathname } from '@ui/hooks/router';
import { COIN_MODE, useCoinMode } from '@ui/hooks/useCoinMode';
import { HintType } from '@ui/hooks/useShowHowItWorks';
import { useTranslations } from '@ui/i18n';
import router from '@ui/overrides/next/router';
import { formatNumber } from '@ui/utils/format';
import { useStrategyTvl } from '@ui/utils/strategy';

import { StakeView } from '../Stake';
import { DepositAndWithdraw } from '../Strategy/DepositAndWithdraw';
import { useStrategyHowItWorks } from '../Strategy/strategyHowItWorks';
import UseLcroDirectly from '../UseLcroDirectly';
import { useStakeHint } from './howItWorks';

export const StakeAndUseLcroDesktop = () => {
  const t = useTranslations();
  const globalCoinMode = useCoinMode();
  const coinModeInfo = COIN_MODE[globalCoinMode];
  const realPathName = useRealPathname();
  const pathInfo = getPathInfo(realPathName);
  const strategyTvl = useStrategyTvl();

  const isStrategy = pathInfo.category === 'strategy';
  const stakeHintForStake = useStakeHint();
  const stakeHintForStrategy = useStrategyHowItWorks();
  const stakeHint = isStrategy ? stakeHintForStrategy : stakeHintForStake;

  return (
    <>
      <DesktopBg
        pattern={globalCoinMode === 'atom' ? 'PATTERN_3_ATOM' : 'PATTERN_3'}
      />
      <HowItWorksDrawer hintType={HintType.STAKE} {...stakeHint} />
      <WithWallet mt="7px" connectHint={t('Connect wallet to see dashboard')}>
        <Flex flexDir="column" gap="19px" alignItems="center">
          <Flex gap={1} w="full">
            <StakeModeSwitchTab
              mr="10px"
              onModeChange={(coinMode, isStrategy) => {
                router.push(
                  isStrategy
                    ? pathDict[coinMode].strategy.deposit
                    : pathDict[coinMode].stake,
                );
              }}
            >
              {(coinMode, isStrategy) =>
                isStrategy
                  ? t('Liquidity Strategy')
                  : t('Stake {coin}', {
                      coin: COIN_MODE[coinMode].name,
                    })
              }
            </StakeModeSwitchTab>
            {!isStrategy ? (
              <>
                <Card flexGrow="1" width={0} px={6} pt={4} mx={0} mr="10px">
                  <Text textStyle="h3">
                    {t('Stake {coin}', {
                      coin: coinModeInfo.name,
                    })}
                  </Text>
                  <StakeView />
                </Card>
                <Card flexGrow="1" width={0} px={6} pt={4} mx={0} ml="10px">
                  <Text textStyle="h3">
                    {t('Use {coin}', {
                      coin: coinModeInfo.liquidName,
                    })}
                  </Text>
                  <UseLcroDirectly />
                </Card>
              </>
            ) : (
              <>
                <Card w="480px" ml="200px" px={6} pt={4}>
                  <Flex mb={4} flexDir="column" align="center">
                    <Text mb={0.5}>
                      {t('TVL')}: {formatNumber(strategyTvl.data ?? 0, 2)} USD
                    </Text>
                    <Flex gap={2} align="center" justifyContent="center">
                      <Text textStyle="caption">
                        {t('Liquidity strategy powered by')}
                      </Text>
                      <Image
                        w="76px"
                        h="16px"
                        src="/images/ferro-logo.png"
                        alt="Ferro"
                      />
                    </Flex>
                  </Flex>
                  <DepositAndWithdraw />
                </Card>
              </>
            )}
          </Flex>
        </Flex>
      </WithWallet>
    </>
  );
};
